import { format, isValid, parseISO } from "date-fns";
import type {
  SocialListeningCompany,
  SocialListeningRunDetails,
} from "src/services/SocialListeningAPIs";
import { asArray, asRecord, asStringArray, toNumber } from "./JsonUtils";
import type {
  BrandListeningReportData,
  CloudWord,
  DriverRow,
  EngagementRow,
  KpiItem,
  LeadNote,
  NextStep,
  Sentiment,
  SentimentBar,
  SentimentShare,
  SourceCoverageRow,
  ThemeComparisonRow,
  ThemeScore,
  TopPost,
  VoiceColumn,
} from "../Types/ListeningReportTypes";
import type { JsonRecord } from "../Types/SocialListeningTypes";

/** A company whose agent report came back, with its row from `comparison.sentiment`. */
interface Brand {
  name: string;
  items: number;
  report: JsonRecord;
  sentiment: JsonRecord;
  analytics: JsonRecord;
}

const LOW_SAMPLE_ITEMS = 50;
const TIE_MARGIN = 0.5;
const MAX_TOP_POSTS = 5;
const MAX_VOICES = 3;
const MAX_STEPS = 6;
const MAX_WINS = 3;
const MAX_TITLE_LENGTH = 80;
const ENOUGH_DATA = "enough_data";

const SENTIMENT_BY_NAME: Record<string, Sentiment> = {
  positive: "pos",
  negative: "neg",
};

const EFFORT_BY_NAME: Record<string, NonNullable<NextStep["effort"]>> = {
  low: { label: "Low effort", level: "lo" },
  medium: { label: "Medium effort", level: "md" },
  high: { label: "High effort", level: "hi" },
};

const EMPTY_SCORE: ThemeScore = { net: 0, praise: 0, complaints: 0, mentions: 0 };

const str = (value: unknown) => (typeof value === "string" ? value.trim() : "");
const has = (value: unknown) => value !== null && value !== undefined;
const nullableNumber = (value: unknown) => (has(value) ? toNumber(value) : null);
const records = (value: unknown): JsonRecord[] =>
  asArray(value)
    .map(asRecord)
    .filter((row): row is JsonRecord => row !== null);
const pct = (value: number) => `${value.toFixed(1)}%`;
const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
const stripMarkdown = (text: string) =>
  text
    .replace(/^\s*(?:[-*+]|#{1,6})\s+/, "")
    .replace(/\*\*/g, "")
    .trim();
const toLabel = (value: unknown) =>
  str(value).split("_").filter(Boolean).map(capitalize).join(" ");
const safeUrl = (value: unknown) => (/^https?:\/\//i.test(str(value)) ? str(value) : undefined);
const sameName = (a: unknown, b: string) => str(a).toLowerCase() === b.toLowerCase();

const toShare = (sentiment: JsonRecord): SentimentShare => {
  const total = toNumber(sentiment.total_count ?? sentiment.items_scored);
  const part = (key: string) => {
    if (has(sentiment[`${key}_percentage`])) {
      return toNumber(sentiment[`${key}_percentage`]);
    }
    return total > 0 ? (toNumber(sentiment[`${key}_count`]) / total) * 100 : 0;
  };
  return { positive: part("positive"), neutral: part("neutral"), negative: part("negative") };
};

const isUsable = (company: SocialListeningCompany) =>
  company.status !== "failed" && asRecord(company.report) !== null;

const toBrand = (company: SocialListeningCompany, sentimentRows: JsonRecord[]): Brand => {
  const report = asRecord(company.report) ?? {};
  const row = sentimentRows.find(item => sameName(item.company_name, company.company_name));
  const sentiment = row && has(row.items_scored) ? row : asRecord(report.overall_sentiment) ?? {};
  return {
    name: company.company_name || str(report.company_name) || "Brand",
    items: toNumber(sentiment.items_scored ?? sentiment.total_count),
    report,
    sentiment,
    analytics: asRecord(report.report_analytics) ?? {},
  };
};

const buildPlatformBars = (brand: Brand): SentimentBar[] =>
  records(brand.analytics.sentiment_by_source).map(row => ({
    name: toLabel(row.source),
    items: toNumber(row.items_scored),
    share: toShare(row),
  }));

const buildBrandBars = (rows: JsonRecord[]): SentimentBar[] =>
  rows
    .filter(row => has(row.items_scored))
    .map(row => {
      const items = toNumber(row.items_scored);
      return {
        name: str(row.company_name),
        items,
        share: toShare(row),
        flag: items < LOW_SAMPLE_ITEMS ? "Small sample" : undefined,
      };
    });

const buildPlatformNote = (bars: SentimentBar[]): LeadNote => {
  const worst = [...bars].sort((a, b) => b.share.negative - a.share.negative)[0];
  return worst
    ? {
        lead: `${worst.name} has the highest negative share:`,
        body: `${pct(worst.share.negative)} of ${worst.items} scored items.`,
      }
    : { lead: "No source data.", body: "" };
};

const buildCompetitiveNote = (bars: SentimentBar[]): LeadNote => {
  const [leader, ...others] = [...bars].sort(
    (a, b) => b.share.positive - b.share.negative - (a.share.positive - a.share.negative)
  );
  const rivals = others
    .map(other => `${other.name}'s ${pct(other.share.positive)} and ${pct(other.share.negative)}`)
    .join("; ");
  return {
    lead: `${leader.name} leads on sentiment:`,
    body: `${pct(leader.share.positive)} positive and ${pct(leader.share.negative)} negative, against ${rivals}.`,
  };
};

const buildKpis = (primary: Brand, competitor?: Brand): KpiItem[] => {
  const share = toShare(primary.sentiment);
  const rival = competitor && toShare(competitor.sentiment);
  const kpis = asRecord(primary.analytics.kpis) ?? {};
  const risk = asRecord(primary.sentiment.top_risk_theme) ?? asRecord(kpis.top_risk_theme);
  const riskCounts = asRecord(risk?.sentiment_counts);
  const compare = (key: keyof SentimentShare) =>
    competitor && rival ? `vs ${competitor.name} ${pct(rival[key])}` : "";
  const trend = (key: "positive" | "negative"): KpiItem["trend"] => {
    if (!rival) {
      return undefined;
    }
    const higher = share[key] >= rival[key];
    return {
      tone: higher === (key === "positive") ? "pos" : "neg",
      direction: higher ? "up" : "down",
    };
  };

  return [
    {
      id: "items",
      label: `${primary.name} Items Scored`,
      help: "Posts and comments that were scored for sentiment",
      icon: "messages",
      value: String(primary.items),
      detail: `${toNumber(primary.sentiment.posts ?? kpis.posts)} posts`,
      compare: `plus ${toNumber(primary.sentiment.comments ?? kpis.comments)} comments`,
    },
    {
      id: "positive",
      label: `${primary.name} Positive`,
      help: "Share of scored items with positive sentiment",
      icon: "positive",
      value: pct(share.positive),
      trend: trend("positive"),
      detail: `${toNumber(primary.sentiment.positive_count)} positive items`,
      compare: compare("positive"),
    },
    {
      id: "negative",
      label: `${primary.name} Negative`,
      help: "Share of scored items with negative sentiment",
      icon: "negative",
      value: pct(share.negative),
      trend: trend("negative"),
      detail: `${toNumber(primary.sentiment.negative_count)} negative items`,
      compare: compare("negative"),
    },
    {
      id: "risk",
      label: "Top Risk Theme",
      help: "Theme with the most negative mentions",
      icon: "risk",
      value: str(risk?.theme) || "–",
      trend: risk ? { tone: "neg", direction: "down" } : undefined,
      detail: `${toNumber(risk?.mention_count)} mentions`,
      compare: `${toNumber(riskCounts?.negative)} negative`,
    },
  ];
};

const buildCoverage = (rows: JsonRecord[], brands: Brand[]): SourceCoverageRow[] =>
  rows
    .map(row => {
      const collected = row.status === "collected";
      const scoredItems = toNumber(row.items_scored);
      const brandItems = brands.find(brand => sameName(row.company_name, brand.name))?.items ?? 0;
      return {
        brand: str(row.company_name),
        source: toLabel(row.source),
        collected,
        posts: toNumber(row.posts),
        comments: toNumber(row.comments),
        scoredItems: collected ? scoredItems : undefined,
        scoredPercent: collected && brandItems > 0 ? (scoredItems / brandItems) * 100 : undefined,
      };
    })
    .sort((a, b) => Number(b.collected) - Number(a.collected));

const buildEngagement = (rows: JsonRecord[]): EngagementRow[] =>
  rows.map(row => ({
    brand: str(row.company_name),
    source: toLabel(row.source),
    posts: toNumber(row.posts),
    likes: nullableNumber(row.likes),
    comments: nullableNumber(row.comments),
    shares: nullableNumber(row.shares),
    averageEngagement: nullableNumber(row.average_known_engagement_per_post),
  }));

const toPostTitle = (text: string, fallback: string) => {
  const line = text.split("\n")[0].trim();
  if (!line) {
    return fallback;
  }
  return line.length > MAX_TITLE_LENGTH ? `${line.slice(0, MAX_TITLE_LENGTH).trimEnd()}…` : line;
};

const buildTopPosts = (rows: JsonRecord[]): TopPost[] =>
  [...rows]
    .sort((a, b) => toNumber(b.engagement_score) - toNumber(a.engagement_score))
    .slice(0, MAX_TOP_POSTS)
    .map(post => {
      const brand = str(post.company_name);
      const source = toLabel(post.source);
      const mood = str(post.dominant_comment_sentiment);
      const tone: TopPost["sentiment"]["tone"] =
        mood === "positive" ? "pos" : mood === "negative" ? "neg" : undefined;
      return {
        title: toPostTitle(str(post.post_text), `${brand} ${source} post`),
        brand,
        source,
        likes: nullableNumber(post.likes),
        comments: nullableNumber(post.comments),
        shares: nullableNumber(post.shares),
        sentiment: { label: `Mostly ${mood || "neutral"}`, tone },
        url: safeUrl(post.post_url),
      };
    });

const buildDrivers = (brand: Brand): DriverRow[] =>
  records(brand.analytics.theme_drivers).map(theme => {
    const counts = asRecord(theme.sentiment_counts) ?? {};
    return {
      name: str(theme.theme),
      note: `${toNumber(theme.mention_count)} mentions`,
      positive: toNumber(counts.positive),
      neutral: toNumber(counts.neutral),
      negative: toNumber(counts.negative),
    };
  });

const toScore = (entry?: JsonRecord): ThemeScore => {
  if (!entry) {
    return EMPTY_SCORE;
  }
  const counts = asRecord(entry.sentiment_counts) ?? {};
  return {
    net: toNumber(entry.net_per_100_items),
    praise: toNumber(counts.positive),
    complaints: toNumber(counts.negative),
    mentions: toNumber(entry.mention_count),
  };
};

/** Pairs the primary brand with its first competitor; a company missing from a theme has 0 mentions. */
const buildThemeComparison = (
  rows: JsonRecord[],
  primary: Brand,
  competitor?: Brand
): ThemeComparisonRow[] => {
  if (!competitor) {
    return [];
  }
  return rows.flatMap(row => {
    const entries = records(row.companies);
    const own = entries.find(entry => sameName(entry.company_name, primary.name));
    const rival = entries.find(entry => sameName(entry.company_name, competitor.name));
    if (!own && !rival) {
      return [];
    }
    const primaryScore = toScore(own);
    const competitorScore = toScore(rival);
    const gap = primaryScore.net - competitorScore.net;
    let leader = "Too close";
    if (Math.abs(gap) >= TIE_MARGIN) {
      leader = gap > 0 ? primary.name : competitor.name;
    }
    return [
      {
        theme: str(row.theme),
        primary: primaryScore,
        competitor: competitorScore,
        leader,
        lowSample: own?.confidence !== ENOUGH_DATA || rival?.confidence !== ENOUGH_DATA,
      },
    ];
  });
};

const buildWins = (drivers: DriverRow[]): LeadNote[] =>
  drivers
    .filter(driver => driver.positive > driver.negative)
    .sort((a, b) => b.positive - b.negative - (a.positive - a.negative))
    .slice(0, MAX_WINS)
    .map(driver => ({
      lead: `${driver.name} is net-positive.`,
      body: `${driver.positive} positive vs ${driver.negative} negative mentions.`,
    }));

const buildVoices = (brand: Brand): VoiceColumn[] =>
  records(asRecord(brand.analytics.customer_voice)?.themes)
    .sort((a, b) => toNumber(b.mention_count) - toNumber(a.mention_count))
    .map((theme): VoiceColumn | null => {
      const counts = asRecord(theme.sentiment_counts) ?? {};
      const examples = asRecord(theme.examples) ?? {};
      const risky = toNumber(theme.net_sentiment) < 0;
      const mood = risky ? "negative" : "positive";
      const quotes = records(examples[mood])
        .map(example => ({
          text: str(example.text),
          topic: str(theme.theme),
          url: safeUrl(example.comment_url) ?? safeUrl(example.post_url),
        }))
        .filter(quote => quote.text);
      if (!quotes.length) {
        return null;
      }
      const tone = risky ? "neg" : "pos";
      const total = toNumber(counts[mood]);
      return {
        id: `${brand.name}-${str(theme.theme_key)}`,
        title: `${brand.name}: ${str(theme.theme)}`,
        countLabel: `${total} ${mood} mentions`,
        badge: { label: risky ? "Risk" : "Strength", tone },
        sentiment: { label: risky ? "Negative" : "Positive", tone },
        total,
        quotes,
      };
    })
    .filter((column): column is VoiceColumn => column !== null)
    .slice(0, MAX_VOICES);

const buildWords = (brand: Brand): CloudWord[] =>
  records(asRecord(brand.analytics.word_cloud)?.words).map(word => ({
    word: str(word.word),
    weight: toNumber(word.count),
    sentiment: SENTIMENT_BY_NAME[str(word.dominant_sentiment)] ?? "neu",
  }));

const firstSentence = (text: string) => text.split(/[.!?](?:\s|$)/)[0].trim();

/** The agent sometimes returns titles with stray markdown (e.g. `Risks**…`); fall back to the description then. */
const buildRecommendationSteps = (brand: Brand): NextStep[] =>
  records(brand.analytics.recommendation_cards)
    .slice(0, MAX_STEPS)
    .map((card, index) => {
      const rawTitle = str(card.title);
      const body = stripMarkdown(str(card.description));
      const title = !rawTitle || rawTitle.includes("**") ? firstSentence(body) : rawTitle;
      return {
        priority: `Priority ${toNumber(card.priority, index + 1)}`,
        effort: EFFORT_BY_NAME[str(card.effort).toLowerCase()],
        title,
        body: body === title ? "" : body,
        tags: [],
      };
    })
    .filter(step => step.title);

/** Older reports only ship one markdown blob; keep the bullets under "Recommended actions". */
const buildLegacySteps = (brand: Brand): NextStep[] => {
  const bullets: string[] = [];
  let inActions = false;
  asArray(brand.report.recommended_actions)
    .flatMap(item => str(item).split("\n"))
    .forEach(line => {
      const heading = /^\*\*(.+?)\*\*:?$/.exec(line.trim());
      if (heading) {
        inActions = /action/i.test(heading[1]);
        return;
      }
      const text = stripMarkdown(line);
      if (inActions && text && !bullets.includes(text)) {
        bullets.push(text);
      }
    });

  return bullets.slice(0, MAX_STEPS).map((text, index) => {
    const title = text.split(/[,:;.]/)[0].trim();
    return { priority: `Priority ${index + 1}`, title, body: title === text ? "" : text, tags: [] };
  });
};

const buildNextSteps = (brand: Brand): NextStep[] => {
  const steps = buildRecommendationSteps(brand);
  return steps.length ? steps : buildLegacySteps(brand);
};

const formatTimestamp = (value: string | null | undefined) => {
  const date = parseISO(value ?? "");
  return isValid(date) ? format(date, "yyyy-MM-dd HH:mm") : "";
};

const buildMeta = (details: SocialListeningRunDetails, primary: Brand) => {
  const { header } = details;
  const sources = asStringArray(primary.report.sources_analyzed).map(toLabel).join(", ");
  return [
    { label: "Request ID", value: header.request_id ?? details.request_id ?? "" },
    { label: "Run timestamp", value: formatTimestamp(header.run_timestamp) },
    { label: `Available ${primary.name} sources`, value: sources },
    { label: "Data window", value: toLabel(header.analysis_window?.requested_label) },
    { label: "Generated by", value: details.created_by?.name ?? header.requested_by?.name ?? "" },
  ].filter(item => item.value);
};

/** Companies that finished without data, listed so the report can say what is missing. */
const getUnavailableCompanies = (details: SocialListeningRunDetails): string[] =>
  details.status === "pending"
    ? []
    : details.companies.filter(company => !isUsable(company)).map(company => company.company_name);

export const buildBrandListeningReport = (
  details: SocialListeningRunDetails | null
): BrandListeningReportData | null => {
  if (!details) {
    return null;
  }
  const comparison = details.comparison ?? {};
  const sentimentRows = records(comparison.sentiment);
  const ordered = [...details.companies].sort(
    (a, b) => Number(b.role === "primary") - Number(a.role === "primary")
  );
  const primaryCompany = ordered[0];
  if (!primaryCompany || !isUsable(primaryCompany)) {
    return null;
  }

  const brands = ordered.filter(isUsable).map(company => toBrand(company, sentimentRows));
  const [primary, competitor] = brands;
  const platformBars = buildPlatformBars(primary);
  const brandBars = buildBrandBars(sentimentRows);
  const drivers = buildDrivers(primary);
  const generated = parseISO(details.header.run_timestamp ?? details.created_at);
  const warnings = brands.flatMap(brand => asStringArray(brand.report.data_sufficiency_warnings));
  const unavailable = getUnavailableCompanies(details);
  const requestId = details.header.request_id ?? details.request_id ?? "";
  const summary = (brand: Brand) => ({
    name: brand.name,
    scoredItems: brand.items,
    sources: asStringArray(brand.report.sources_analyzed).map(toLabel),
  });

  return {
    title: `${primary.name} Brand Listening Report`,
    primaryBrand: summary(primary),
    competitor: competitor && summary(competitor),
    meta: buildMeta(details, primary),
    generatedOn: isValid(generated) ? format(generated, "d MMM yyyy") : "",
    requestId,
    unavailableNotice: unavailable.length
      ? {
          title: "Some brands could not be analysed.",
          body: `Data is unavailable for ${unavailable.join(", ")}, so they are left out of this report.`,
        }
      : undefined,
    limitedDataNotice: warnings.length
      ? {
          title: "This report was compiled on limited data, so metrics may be skewed.",
          body: warnings.join(" "),
        }
      : undefined,
    kpis: buildKpis(primary, competitor),
    readout: str(primary.report.narrative_summary)
      .split("\n")
      .map(stripMarkdown)
      .filter(Boolean)
      .map(text => [{ text }]),
    competitiveSentiment:
      brandBars.length > 1
        ? { bars: brandBars, note: buildCompetitiveNote(brandBars) }
        : undefined,
    platformSentiment: { bars: platformBars, note: buildPlatformNote(platformBars) },
    sourceCoverage: buildCoverage(records(comparison.source_coverage), brands),
    sourceCoverageNote:
      "Every source the agent checked for each brand, including those that returned nothing.",
    engagementBySource: buildEngagement(records(comparison.engagement_by_source)),
    topPosts: buildTopPosts(records(comparison.top_engagement_posts)),
    drivers,
    driverInsights: [],
    themeComparison: buildThemeComparison(records(comparison.theme_comparison), primary, competitor),
    wins: buildWins(drivers),
    voices: buildVoices(primary),
    words: buildWords(primary),
    nextSteps: buildNextSteps(primary),
  };
};
